import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  Heading,
  HStack,
  Icon,
  ListItem,
  Tab,
  Tabs,
  TabList,
  TabPanel,
  TabPanels,
  Text,
  Textarea,
  UnorderedList,
  useClipboard,
  VStack,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { MdAdd, MdContentCopy, MdHome, MdOpenInNew } from "react-icons/md";
import { FaPhone } from "react-icons/fa";
import { CelebrationBurst } from "../Illustrations";

// Build embed snippet variants for a chat widget. Matches the shapes
// emitted by the existing Code.js modal so users can drop these onto
// their site exactly the same way.
function chatEmbedSnippets({ host, widgetId, assistantName }) {
  const floating = `<!-- ${assistantName} chat widget -->
<link rel="stylesheet" href="https://${host}/chat-widget-v2/style.css" />
<script src="https://${host}/chat-widget-v2/script.js"></script>
<script>
  new ChatWidget({
    widgetId: "${widgetId}",
    deployType: "floating"
  }).init();
</script>`;

  const iframe = `<!-- ${assistantName} chat widget (iframe) -->
<iframe
  src="https://${host}/chat-widget-v2/index.html?widgetId=${widgetId}"
  allow="microphone"
  style="border: none; display: block; width: 100%; height: 600px;"
></iframe>`;

  const link = `https://${host}/bot-iframe.html?widgetId=${widgetId}`;
  return { floating, iframe, link };
}

function webCallEmbedSnippets({ host, widgetId, assistantName }) {
  const iframe = `<!-- ${assistantName} web-call widget -->
<iframe
  id="webCallIframe"
  allow="microphone"
  src="https://${host}/web-call-iframe.html?widgetId=${widgetId}"
  style="border: none; width: 100%; height: 600px;"
></iframe>`;

  const link = `https://${host}/web-call-iframe.html?widgetId=${widgetId}`;
  return { iframe, link };
}

function CodeBlock({ code }) {
  const { hasCopied, onCopy } = useClipboard(code);
  return (
    <Box position="relative">
      <Textarea
        readOnly
        value={code}
        rows={Math.min(code.split("\n").length + 1, 14)}
        fontFamily="mono"
        fontSize="xs"
        bg="gray.50"
        pr={20}
      />
      <Button
        size="xs"
        position="absolute"
        top={2}
        right={2}
        leftIcon={<MdContentCopy />}
        onClick={onCopy}
        colorScheme={hasCopied ? "green" : "gray"}
      >
        {hasCopied ? "Copied" : "Copy"}
      </Button>
    </Box>
  );
}

function ChatDeployedSection({ widgetUrl, embedCode, assistantName }) {
  const linkClip = useClipboard(embedCode.link);
  return (
    <VStack spacing={4} align="stretch">
      {/* Live embedded preview */}
      <Card>
        <CardBody p={0} overflow="hidden" borderRadius="md">
          <Box bg="gray.100" px={4} py={2} borderBottomWidth="1px">
            <HStack justify="space-between">
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                Live preview
              </Text>
              <HStack spacing={2}>
                <Button
                  size="xs"
                  variant="outline"
                  leftIcon={<MdContentCopy />}
                  onClick={linkClip.onCopy}
                >
                  {linkClip.hasCopied ? "Copied" : "Copy link"}
                </Button>
                <Button
                  as="a"
                  href={widgetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                  colorScheme="blue"
                  rightIcon={<MdOpenInNew />}
                >
                  Open in new tab
                </Button>
              </HStack>
            </HStack>
          </Box>
          <Box height="500px" bg="white">
            <iframe
              title={`${assistantName} chat preview`}
              src={widgetUrl}
              allow="microphone"
              style={{ border: "none", width: "100%", height: "100%" }}
            />
          </Box>
        </CardBody>
      </Card>

      {/* Embed code */}
      <Card>
        <CardBody>
          <Heading size="sm" mb={3}>
            Embed on your site
          </Heading>
          <Tabs size="sm" variant="enclosed">
            <TabList>
              <Tab>Floating bubble</Tab>
              <Tab>Inline iframe</Tab>
              <Tab>Shareable link</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Drop this anywhere on your page. The bubble appears in the
                  bottom-right corner.
                </Text>
                <CodeBlock code={embedCode.floating} />
              </TabPanel>
              <TabPanel px={0}>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Drops the chatbot inline wherever you place the iframe.
                </Text>
                <CodeBlock code={embedCode.iframe} />
              </TabPanel>
              <TabPanel px={0}>
                <Text fontSize="xs" color="gray.600" mb={2}>
                  Send this URL to anyone — no installation required.
                </Text>
                <Box bg="gray.50" p={3} borderRadius="md" fontFamily="mono" fontSize="xs">
                  <Code colorScheme="blue" bg="transparent" p={0}>
                    {embedCode.link}
                  </Code>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </VStack>
  );
}

function VoiceDeployedSection({
  phoneNumber,
  webCallWidgetUrl,
  webCallEmbed,
  assistantName,
}) {
  const phoneClip = useClipboard(phoneNumber || "");
  return (
    <VStack spacing={4} align="stretch">
      {/* Phone number card */}
      <Card>
        <CardBody py={6}>
          <VStack spacing={3}>
            <Text fontSize="sm" color="gray.500" textTransform="uppercase" fontWeight="bold">
              Call to test
            </Text>
            <HStack spacing={3}>
              <Icon as={FaPhone} color="purple.500" boxSize={6} />
              <Heading size="lg" fontFamily="mono">
                {phoneNumber || "—"}
              </Heading>
            </HStack>
            <HStack>
              <Button
                size="sm"
                leftIcon={<MdContentCopy />}
                onClick={phoneClip.onCopy}
                isDisabled={!phoneNumber}
              >
                {phoneClip.hasCopied ? "Copied" : "Copy number"}
              </Button>
              {phoneNumber && (
                <Button
                  as="a"
                  href={`tel:${phoneNumber}`}
                  size="sm"
                  colorScheme="purple"
                  leftIcon={<FaPhone />}
                >
                  Call now
                </Button>
              )}
            </HStack>
            <Text fontSize="sm" color="gray.500" textAlign="center" maxW="md">
              Try saying: <em>"Hi, I'd like to book an appointment for next week."</em>
            </Text>
          </VStack>
        </CardBody>
      </Card>

      {/* Web-call widget preview */}
      {webCallWidgetUrl && (
        <Card>
          <CardBody p={0} overflow="hidden" borderRadius="md">
            <Box bg="gray.100" px={4} py={2} borderBottomWidth="1px">
              <HStack justify="space-between">
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  Test in your browser (no phone needed)
                </Text>
                <Button
                  as="a"
                  href={webCallWidgetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                  variant="outline"
                  rightIcon={<MdOpenInNew />}
                >
                  Open in new tab
                </Button>
              </HStack>
            </Box>
            <Box height="500px" bg="white">
              <iframe
                title={`${assistantName} web-call preview`}
                src={webCallWidgetUrl}
                allow="microphone"
                style={{ border: "none", width: "100%", height: "100%" }}
              />
            </Box>
          </CardBody>
        </Card>
      )}

      {/* Embed code for the web call */}
      {webCallEmbed && (
        <Card>
          <CardBody>
            <Heading size="sm" mb={3}>
              Embed the in-browser call on your site
            </Heading>
            <Tabs size="sm" variant="enclosed">
              <TabList>
                <Tab>Inline iframe</Tab>
                <Tab>Shareable link</Tab>
              </TabList>
              <TabPanels>
                <TabPanel px={0}>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Embed a click-to-call button on your contact page.
                  </Text>
                  <CodeBlock code={webCallEmbed.iframe} />
                </TabPanel>
                <TabPanel px={0}>
                  <Text fontSize="xs" color="gray.600" mb={2}>
                    Send this link to anyone — works from any browser.
                  </Text>
                  <Box bg="gray.50" p={3} borderRadius="md" fontFamily="mono" fontSize="xs">
                    <Code colorScheme="purple" bg="transparent" p={0}>
                      {webCallEmbed.link}
                    </Code>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
}

export default function Step7Test({ draft, deployResult, onBuildAnother, onDone }) {
  const isVoice = draft.channel === "voice";
  const errors = deployResult?.errors || [];
  const hasErrors = errors.length > 0;
  const host = deployResult?.chatbotHost || "cdn.insighto.ai";
  const assistantName = deployResult?.assistantName || "your agent";

  const chatEmbed = deployResult?.widgetId
    ? chatEmbedSnippets({
        host,
        widgetId: deployResult.widgetId,
        assistantName,
      })
    : null;

  const webCallEmbed = deployResult?.webCallWidgetId
    ? webCallEmbedSnippets({
        host,
        widgetId: deployResult.webCallWidgetId,
        assistantName,
      })
    : null;

  return (
    <VStack spacing={6} align="stretch">
      <VStack spacing={2} textAlign="center">
        <CelebrationBurst size={120} />
        <Heading size="lg">
          {hasErrors ? "Your agent is mostly live" : "Your agent is live!"}
        </Heading>
        <Text color="gray.600">
          {isVoice
            ? "Call the number below, or test in your browser — both connect to the same agent."
            : "Try it below — it's already running."}
        </Text>
      </VStack>

      {hasErrors && (
        <Alert status="warning" borderRadius="md" alignItems="start">
          <AlertIcon mt={1} />
          <Box>
            <Text fontWeight="semibold" fontSize="sm" mb={1}>
              A few steps didn't finish cleanly — your assistant exists but
              you'll need to wrap these up manually:
            </Text>
            <UnorderedList fontSize="sm" spacing={1}>
              {errors.map((err, i) => (
                <ListItem key={i}>{err}</ListItem>
              ))}
            </UnorderedList>
            <Text fontSize="xs" color="gray.600" mt={2}>
              Open the assistant below to retry these from the Assistants
              page.
            </Text>
          </Box>
        </Alert>
      )}

      {isVoice ? (
        <VoiceDeployedSection
          phoneNumber={deployResult?.phoneNumber}
          webCallWidgetUrl={deployResult?.webCallWidgetUrl}
          webCallEmbed={webCallEmbed}
          assistantName={assistantName}
        />
      ) : chatEmbed ? (
        <ChatDeployedSection
          widgetUrl={deployResult.widgetUrl}
          embedCode={chatEmbed}
          assistantName={assistantName}
        />
      ) : (
        <Card>
          <CardBody>
            <Text color="gray.500">Widget URL not available.</Text>
          </CardBody>
        </Card>
      )}

      {/* Next-step affordances. Everything below already exists on the
          server — these buttons clear the local wizard state so the user
          can move on without the "discard your progress" dialog. */}
      <Box bg="blue.50" borderRadius="md" p={5}>
        <VStack spacing={3}>
          <Text fontSize="sm" color="gray.700" textAlign="center">
            Your assistant, widgets, and integrations are all saved. What's next?
          </Text>
          <HStack spacing={3} flexWrap="wrap" justify="center">
            <Button
              colorScheme="blue"
              leftIcon={<MdAdd />}
              onClick={onBuildAnother}
            >
              Build another agent
            </Button>
            <Button
              as={RouterLink}
              to={
                deployResult?.assistantId
                  ? `/ai-agents/assistants/${deployResult.assistantId}`
                  : "/ai-agents/assistants"
              }
              variant="outline"
              rightIcon={<MdOpenInNew />}
            >
              Open in Assistants
            </Button>
            <Button
              variant="ghost"
              leftIcon={<MdHome />}
              onClick={onDone}
            >
              Back to dashboard
            </Button>
          </HStack>
        </VStack>
      </Box>
    </VStack>
  );
}
